import React, { useState, useCallback, useRef, useEffect } from 'react';
import styled, { keyframes, css } from 'styled-components';

// ─── Animations ──────────────────────────────────────────────────────────────
const rainbow = keyframes`
  0%{background-position:0% 50%}50%{background-position:100% 50%}100%{background-position:0% 50%}
`;
const popIn = keyframes`
  0%{transform:scale(0.5) translateY(30px);opacity:0}
  70%{transform:scale(1.05) translateY(-4px);opacity:1}
  100%{transform:scale(1) translateY(0);opacity:1}
`;
const correctBurst = keyframes`
  0%{transform:scale(1);box-shadow:0 0 0 0 rgba(34,197,94,0.7)}
  50%{transform:scale(1.04);box-shadow:0 0 0 20px rgba(34,197,94,0)}
  100%{transform:scale(1);box-shadow:0 0 0 0 rgba(34,197,94,0)}
`;
const wrongShake = keyframes`
  0%,100%{transform:translateX(0)}
  20%{transform:translateX(-10px)}40%{transform:translateX(10px)}
  60%{transform:translateX(-7px)}80%{transform:translateX(7px)}
`;
const floatUp = keyframes`
  0%{transform:translateY(0) scale(1);opacity:1}
  100%{transform:translateY(-100px) scale(1.5);opacity:0}
`;
const pulse = keyframes`
  0%,100%{opacity:1}50%{opacity:0.6}
`;
const slideDown = keyframes`
  from{transform:translateY(-20px);opacity:0}to{transform:translateY(0);opacity:1}
`;
const starPop = keyframes`
  0%{transform:scale(0) rotate(0deg);opacity:0}
  60%{transform:scale(1.4) rotate(20deg);opacity:1}
  100%{transform:scale(1) rotate(0deg);opacity:1}
`;

// ─── Styled Components ────────────────────────────────────────────────────────
const PageBg = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, #0f0c29 0%, #302b63 50%, #24243e 100%);
  padding: 16px;
  font-family: 'Segoe UI', Tahoma, sans-serif;
  position: relative;
  overflow: hidden;
  &::before {
    content: '';
    position: fixed; inset: 0;
    background:
      radial-gradient(ellipse at 15% 15%, rgba(139,92,246,0.2) 0%, transparent 55%),
      radial-gradient(ellipse at 85% 85%, rgba(236,72,153,0.15) 0%, transparent 55%);
    pointer-events: none;
  }
`;

const Stars = styled.div`
  position: fixed; inset: 0; pointer-events: none;
  &::before, &::after {
    content: ''; position: absolute; width: 2px; height: 2px; border-radius: 50%;
    background: #fff;
    box-shadow: ${() => Array.from({ length: 90 }, () =>
      `${Math.random() * 100}vw ${Math.random() * 100}vh 1px rgba(255,255,255,${0.15 + Math.random() * 0.6})`
    ).join(',')};
  }
`;

const Title = styled.h1`
  text-align: center;
  font-size: clamp(20px, 4vw, 34px);
  font-weight: 900;
  margin: 0 0 16px;
  background: linear-gradient(90deg, #f59e0b, #ec4899, #8b5cf6, #06b6d4, #10b981);
  background-size: 300% 100%;
  animation: ${rainbow} 5s ease infinite;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  letter-spacing: -0.5px;
`;

const TeacherPanel = styled.div<{ collapsed?: boolean }>`
  background: rgba(255,255,255,0.06);
  border: 1px solid rgba(255,255,255,0.12);
  border-radius: 18px;
  padding: ${p => p.collapsed ? '12px 18px' : '18px'};
  margin-bottom: 20px;
  backdrop-filter: blur(10px);
  transition: all 0.3s ease;
`;

const PanelTitle = styled.div`
  color: #a5f3fc;
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 1px;
  text-transform: uppercase;
  display: flex; align-items: center; gap: 8px;
  cursor: pointer;
  user-select: none;
`;

const Textarea = styled.textarea`
  width: 100%;
  min-height: 150px;
  background: rgba(0,0,0,0.3);
  border: 1px solid rgba(255,255,255,0.15);
  border-radius: 12px;
  color: #f1f5f9;
  font-size: 13px;
  padding: 12px;
  resize: vertical;
  outline: none;
  font-family: monospace;
  line-height: 1.7;
  margin-top: 12px;
  &:focus { border-color: #818cf8; }
  &::placeholder { color: #475569; }
`;

const FormatHint = styled.div`
  margin-top: 8px; color: #64748b; font-size: 11px; line-height: 1.7;
  span { color: #94a3b8; }
`;

const BuildBtn = styled.button`
  margin-top: 12px;
  padding: 10px 28px;
  border: none; border-radius: 12px;
  background: linear-gradient(135deg, #6366f1, #8b5cf6);
  color: #fff; font-size: 14px; font-weight: 700;
  cursor: pointer; transition: all 0.2s;
  box-shadow: 0 4px 16px rgba(99,102,241,0.35);
  &:hover { transform: translateY(-2px); box-shadow: 0 8px 24px rgba(99,102,241,0.5); }
  &:active { transform: translateY(0); }
`;

// Question card
const QuestionArea = styled.div`
  max-width: 860px;
  margin: 0 auto;
  position: relative;
  z-index: 1;
`;

const ProgressRow = styled.div`
  display: flex; align-items: center; gap: 12px; margin-bottom: 16px;
`;
const ProgressBar = styled.div<{ pct: number }>`
  flex: 1; height: 8px; border-radius: 99px;
  background: rgba(255,255,255,0.1);
  position: relative; overflow: hidden;
  &::after {
    content: '';
    position: absolute; top: 0; left: 0;
    height: 100%;
    width: ${p => p.pct}%;
    background: linear-gradient(90deg, #6366f1, #ec4899);
    border-radius: 99px;
    transition: width 0.4s ease;
  }
`;
const ProgressLabel = styled.span`
  color: #94a3b8; font-size: 13px; font-weight: 600; white-space: nowrap;
`;

const QuestionCard = styled.div<{ state?: 'idle'|'correct'|'wrong' }>`
  background: rgba(255,255,255,0.07);
  border: 1.5px solid rgba(255,255,255,0.13);
  border-radius: 24px;
  padding: 28px 32px 24px;
  backdrop-filter: blur(14px);
  animation: ${slideDown} 0.35s ease;
  ${p => p.state === 'correct' && css`animation: ${correctBurst} 0.6s ease-out;`}
  ${p => p.state === 'wrong' && css`animation: ${wrongShake} 0.5s ease;`}
  box-shadow: 0 8px 40px rgba(0,0,0,0.3);
`;

const QuestionNumber = styled.div`
  font-size: 11px; font-weight: 700; color: #818cf8;
  text-transform: uppercase; letter-spacing: 2px; margin-bottom: 10px;
`;

const QuestionText = styled.div`
  font-size: clamp(17px, 3vw, 24px);
  font-weight: 700;
  color: #f1f5f9;
  line-height: 1.5;
  margin-bottom: 24px;
`;

const OptionsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  @media (max-width: 520px) { grid-template-columns: 1fr; }
`;

const OPTION_COLORS = [
  { idle: 'rgba(99,102,241,0.18)', active: '#6366f1', label: '#818cf8' },
  { idle: 'rgba(236,72,153,0.18)', active: '#ec4899', label: '#f472b6' },
  { idle: 'rgba(245,158,11,0.18)', active: '#f59e0b', label: '#fbbf24' },
  { idle: 'rgba(16,185,129,0.18)', active: '#10b981', label: '#34d399' },
];

const OPTION_LABELS = ['A', 'B', 'C', 'D'];

const OptionBtn = styled.button<{
  status: 'idle' | 'selected' | 'correct' | 'wrong' | 'dim';
  colorIdx: number;
}>`
  border: 2px solid transparent;
  border-radius: 16px;
  padding: 14px 18px;
  display: flex; align-items: center; gap: 12px;
  cursor: pointer;
  text-align: left;
  font-size: clamp(13px, 2vw, 16px);
  font-weight: 600;
  transition: all 0.2s;
  position: relative;
  overflow: hidden;
  animation: ${popIn} 0.3s ease;

  ${p => {
    const c = OPTION_COLORS[p.colorIdx];
    if (p.status === 'idle') return css`
      background: ${c.idle};
      color: #e2e8f0;
      border-color: transparent;
      &:hover { border-color: ${c.label}; transform: translateY(-2px); }
    `;
    if (p.status === 'selected') return css`
      background: ${c.active};
      color: #fff;
      border-color: #fff;
      transform: scale(1.03);
    `;
    if (p.status === 'correct') return css`
      background: #16a34a;
      color: #fff;
      border-color: #4ade80;
      animation: ${correctBurst} 0.7s ease-out;
    `;
    if (p.status === 'wrong') return css`
      background: #dc2626;
      color: #fff;
      border-color: #f87171;
      animation: ${wrongShake} 0.45s ease;
    `;
    if (p.status === 'dim') return css`
      background: rgba(255,255,255,0.04);
      color: #475569;
      border-color: transparent;
      cursor: default;
    `;
  }}
`;

const OptionLabel = styled.span<{ colorIdx: number; status: string }>`
  width: 30px; height: 30px;
  border-radius: 8px;
  display: flex; align-items: center; justify-content: center;
  font-weight: 900; font-size: 14px; flex-shrink: 0;
  background: ${p => p.status === 'idle'
    ? OPTION_COLORS[p.colorIdx].label + '33'
    : 'rgba(255,255,255,0.25)'};
  color: ${p => ['idle'].includes(p.status)
    ? OPTION_COLORS[p.colorIdx].label
    : '#fff'};
`;

const RevealBadge = styled.div`
  margin-top: 20px;
  display: flex; align-items: center; gap: 10px;
  padding: 12px 16px;
  background: rgba(34,197,94,0.15);
  border: 1.5px solid #22c55e;
  border-radius: 14px;
  color: #4ade80;
  font-size: 15px;
  font-weight: 700;
  animation: ${popIn} 0.4s ease;
`;

const FloatEmoji = styled.span<{ x: number }>`
  position: fixed;
  bottom: 20%;
  left: ${p => p.x}vw;
  font-size: 2.5rem;
  pointer-events: none;
  animation: ${floatUp} 1.2s ease-out forwards;
  z-index: 100;
`;

const NavRow = styled.div`
  display: flex; align-items: center; justify-content: center; gap: 12px;
  margin-top: 20px; flex-wrap: wrap;
`;

const NavBtn = styled.button<{ variant?: 'primary' | 'ghost' }>`
  padding: 10px 22px;
  border-radius: 12px;
  font-size: 14px; font-weight: 700;
  cursor: pointer; transition: all 0.2s;
  border: 2px solid transparent;
  ${p => p.variant === 'primary' ? css`
    background: linear-gradient(135deg,#6366f1,#8b5cf6);
    color: #fff;
    box-shadow: 0 4px 16px rgba(99,102,241,0.35);
    &:hover { transform: translateY(-2px); }
  ` : css`
    background: rgba(255,255,255,0.08);
    color: #94a3b8;
    border-color: rgba(255,255,255,0.1);
    &:hover { background: rgba(255,255,255,0.14); color: #e2e8f0; }
  `}
  &:disabled { opacity: 0.35; cursor: not-allowed; transform: none; }
`;

const RevealBtn = styled.button`
  padding: 10px 22px;
  border-radius: 12px;
  font-size: 14px; font-weight: 700;
  cursor: pointer; transition: all 0.2s;
  border: 2px solid #f59e0b;
  background: rgba(245,158,11,0.15);
  color: #fbbf24;
  &:hover { background: rgba(245,158,11,0.3); transform: translateY(-2px); }
`;

const ExplainBox = styled.div`
  margin-top: 14px;
  padding: 12px 16px;
  background: rgba(139,92,246,0.12);
  border: 1.5px solid rgba(139,92,246,0.35);
  border-radius: 14px;
  color: #c4b5fd;
  font-size: 14px;
  line-height: 1.6;
  animation: ${slideDown} 0.3s ease;
`;

const ScorePill = styled.div`
  background: rgba(99,102,241,0.2);
  border: 1.5px solid #6366f1;
  border-radius: 99px;
  padding: 4px 14px;
  color: #818cf8;
  font-size: 13px; font-weight: 700;
  display: flex; align-items: center; gap: 6px;
`;

// ─── Types ────────────────────────────────────────────────────────────────────
interface MCQuestion {
  number: string;
  question: string;
  options: string[];    // Up to 4 options text
  answerIdx: number;    // 0-based correct answer index
  explanation?: string;
}

/**
 * Hỗ trợ nhiều định dạng:
 *   1. Question text?              ← câu hỏi bắt đầu bằng số
 *   A. Opt1  B. Opt2  C. Opt3  D. Opt4   ← options cùng dòng
 *   — hoặc mỗi option 1 dòng —
 *   A. Opt1
 *   B. Opt2
 *   Answer: B  /  => B  /  →B  /  *B. đặt trước đáp án đúng
 *   // Giải thích (tuỳ chọn)
 */
function parseQuestions(raw: string): MCQuestion[] {
  const questions: MCQuestion[] = [];
  const lines = raw.split('\n').map(l => l.trim());

  let i = 0;
  while (i < lines.length) {
    const line = lines[i];
    if (!line) { i++; continue; }

    // Must start with a question number: "1." or "1)"
    const qMatch = line.match(/^(\d+)[.)]\s+(.+)/);
    if (!qMatch) { i++; continue; }

    const number = qMatch[1];
    const questionText = qMatch[2];
    i++;

    const rawOptions: string[] = [];
    let answerIdx = -1;
    let explanation = '';

    while (i < lines.length) {
      const l = lines[i];
      if (!l) { i++; continue; }

      // Stop at next question
      if (/^\d+[.)]\s/.test(l)) break;

      // Answer line: "Answer: B" / "=> B" / "→ B"
      if (/^(answer|đáp\s*án|key)\s*:?\s*[A-Da-d]/i.test(l) ||
          /^=>\s*[A-Da-d]/i.test(l) ||
          /^[→➜]\s*[A-Da-d]/i.test(l)) {
        // Find the answer letter AFTER the colon/arrow (not "A" in "Answer")
        const m = l.match(/[:=>→➜]\s*([A-Da-d])\b/i)   // "Answer: B" → B
                || l.match(/\b([A-Da-d])\s*$/i);          // fallback: last letter at end
        if (m) answerIdx = 'ABCD'.indexOf(m[1].toUpperCase());
        i++;
        continue;
      }

      // Explanation: starts with //
      if (/^\/\//.test(l) || /^(explain|giải\s*thích|note)\s*:/i.test(l)) {
        explanation = l.replace(/^(\/\/|explain:|giải thích:|note:)\s*/i, '').trim();
        i++;
        continue;
      }

      // Count option-letter patterns in this line: A. or A)
      const optCount = (l.match(/\b[A-Da-d][.)]/g) || []).length;

      if (optCount >= 2) {
        // Multiple options on ONE line: "A. London  B. Paris  C. Berlin  D. Rome"
        // Split at whitespace before a letter-dot sequence
        const parts = l.split(/\s{2,}|\t|\s+(?=[A-Da-d][.)])/);
        parts.forEach(p => {
          const s = p.trim();
          if (!s) return;
          if (/^\*?[A-Da-d][.)]\s/.test(s)) {
            if (s.startsWith('*')) {
              const m = s.match(/^\*([A-Da-d])/);
              if (m) answerIdx = 'ABCD'.indexOf(m[1].toUpperCase());
            }
            rawOptions.push(s.startsWith('*') ? s.slice(1) : s);
          }
        });
        // Fallback if split gave too few results → try regex extraction
        if (rawOptions.length < 2) {
          rawOptions.length = 0;
          const re = /[*]?([A-Da-d])[.)]\s*([^A-Da-d]+?)(?=\s+[A-Da-d][.)]|$)/g;
          let m;
          while ((m = re.exec(l)) !== null) {
            rawOptions.push(`${m[1]}. ${m[2].trim()}`);
          }
        }
        i++;
        continue;
      }

      if (optCount === 1 && /^\*?[A-Da-d][.)]\s/.test(l)) {
        // Single option on its own line
        const clean = l.startsWith('*') ? l.slice(1) : l;
        if (l.startsWith('*')) {
          const m = l.match(/^\*([A-Da-d])/);
          if (m) answerIdx = 'ABCD'.indexOf(m[1].toUpperCase());
        }
        rawOptions.push(clean);
        i++;
        continue;
      }

      // Unknown line — skip
      i++;
    }

    if (rawOptions.length < 2) continue;

    // Strip the "A. " prefix from each option text
    const options = rawOptions.map(o => o.replace(/^[A-Da-d][.)]\s*/, '').trim());

    if (answerIdx === -1) answerIdx = 0;
    answerIdx = Math.max(0, Math.min(answerIdx, options.length - 1));

    questions.push({
      number,
      question: questionText,
      options: options.slice(0, 4),
      answerIdx,
      explanation: explanation || undefined,
    });
  }
  return questions;
}


// ─── Sound ────────────────────────────────────────────────────────────────────
const playSound = (type: 'correct' | 'wrong' | 'reveal') => {
  try {
    const ctx = new (window.AudioContext || (window as any).webkitAudioContext)();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain); gain.connect(ctx.destination);
    if (type === 'correct') {
      osc.frequency.setValueAtTime(523, ctx.currentTime);
      osc.frequency.setValueAtTime(659, ctx.currentTime + 0.1);
      osc.frequency.setValueAtTime(784, ctx.currentTime + 0.2);
      gain.gain.setValueAtTime(0.3, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.5);
      osc.start(); osc.stop(ctx.currentTime + 0.5);
    } else if (type === 'wrong') {
      osc.frequency.setValueAtTime(220, ctx.currentTime);
      osc.frequency.setValueAtTime(180, ctx.currentTime + 0.15);
      gain.gain.setValueAtTime(0.25, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.4);
      osc.start(); osc.stop(ctx.currentTime + 0.4);
    } else {
      osc.frequency.setValueAtTime(440, ctx.currentTime);
      osc.frequency.setValueAtTime(550, ctx.currentTime + 0.08);
      gain.gain.setValueAtTime(0.2, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.35);
      osc.start(); osc.stop(ctx.currentTime + 0.35);
    }
  } catch { /* ignore */ }
};

// ─── Component ────────────────────────────────────────────────────────────────
const MultipleChoicePage: React.FC = () => {
  const [rawText, setRawText] = useState('');
  const [questions, setQuestions] = useState<MCQuestion[]>([]);
  const [current, setCurrent] = useState(0);
  const [selected, setSelected] = useState<number | null>(null); // option idx user clicked
  const [revealed, setRevealed] = useState(false);
  const [score, setScore] = useState(0);
  const [cardState, setCardState] = useState<'idle'|'correct'|'wrong'>('idle');
  const [floats, setFloats] = useState<{ id: number; emoji: string; x: number }[]>([]);
  const [panelOpen, setPanelOpen] = useState(true);
  const questionRef = useRef<HTMLDivElement>(null);
  const floatId = useRef(0);

  const spawnFloat = useCallback((correct: boolean) => {
    const emojis = correct
      ? ['🌟','⭐','🎉','✨','🔥','🏆','💯','🎊']
      : ['💪','🤔','📚','✍️','🔁'];
    const n = correct ? 5 : 2;
    for (let i = 0; i < n; i++) {
      setTimeout(() => {
        const id = floatId.current++;
        setFloats(f => [...f, {
          id, x: 10 + Math.random() * 80,
          emoji: emojis[Math.floor(Math.random() * emojis.length)],
        }]);
        setTimeout(() => setFloats(f => f.filter(fl => fl.id !== id)), 1300);
      }, i * 120);
    }
  }, []);

  const handleBuild = () => {
    const parsed = parseQuestions(rawText);
    if (parsed.length === 0) {
      alert('Không nhận dạng được câu hỏi nào. Hãy kiểm tra lại định dạng.');
      return;
    }
    setQuestions(parsed);
    setCurrent(0);
    setSelected(null);
    setRevealed(false);
    setScore(0);
    setCardState('idle');
    setPanelOpen(false);
    setTimeout(() => questionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
  };

  const handleSelect = (idx: number) => {
    if (revealed || selected !== null) return;
    setSelected(idx);
    if (idx === questions[current].answerIdx) {
      setCardState('correct');
      setScore(s => s + 1);
      playSound('correct');
      spawnFloat(true);
    } else {
      setCardState('wrong');
      playSound('wrong');
      spawnFloat(false);
    }
    setTimeout(() => setCardState('idle'), 700);
  };

  const handleReveal = () => {
    setRevealed(true);
    playSound('reveal');
  };

  const handleNext = () => {
    if (current < questions.length - 1) {
      setCurrent(c => c + 1);
      setSelected(null);
      setRevealed(false);
      setCardState('idle');
      setTimeout(() => questionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 80);
    }
  };

  const handlePrev = () => {
    if (current > 0) {
      setCurrent(c => c - 1);
      setSelected(null);
      setRevealed(false);
      setCardState('idle');
    }
  };

  const handleReset = () => {
    setCurrent(0);
    setSelected(null);
    setRevealed(false);
    setScore(0);
    setCardState('idle');
  };

  const q = questions[current] ?? null;

  const getOptionStatus = (idx: number): 'idle' | 'selected' | 'correct' | 'wrong' | 'dim' => {
    if (!revealed && selected === null) return 'idle';
    if (!revealed && selected !== null) {
      if (idx === selected) return selected === questions[current].answerIdx ? 'correct' : 'wrong';
      return 'dim';
    }
    // revealed
    if (idx === questions[current].answerIdx) return 'correct';
    if (idx === selected) return 'wrong';
    return 'dim';
  };

  const EXAMPLE = `1. What is the capital of France?
A. London  B. Paris  C. Berlin  D. Rome
Answer: B
// Paris is the capital of France

2. Which verb tense is "She is eating"?
A. Simple Present  B. Past Simple  C. Present Continuous  D. Future
Answer: C`;

  return (
    <PageBg>
      <Stars />

      {/* Float emojis */}
      {floats.map(f => (
        <FloatEmoji key={f.id} x={f.x}>{f.emoji}</FloatEmoji>
      ))}

      <Title>🧠 Trắc Nghiệm Trực Tiếp</Title>

      {/* Teacher Panel */}
      <TeacherPanel collapsed={!panelOpen} style={{ maxWidth: 860, margin: '0 auto 20px' }}>
        <PanelTitle onClick={() => setPanelOpen(o => !o)}>
          <span>📋</span>
          <span>Nhập bài tập ({questions.length > 0 ? `${questions.length} câu` : 'Giáo viên'})</span>
          <span style={{ marginLeft: 'auto', color: '#64748b' }}>{panelOpen ? '▲' : '▼'}</span>
        </PanelTitle>

        {panelOpen && (
          <>
            <Textarea
              placeholder={`Dán câu hỏi vào đây...\n\nVí dụ:\n${EXAMPLE}`}
              value={rawText}
              onChange={e => setRawText(e.target.value)}
            />
            <FormatHint>
              <span>📌 Định dạng hỗ trợ:</span><br />
              • Số thứ tự đầu câu: <span>1.</span> hoặc <span>1)</span><br />
              • Đáp án chọn: <span>A. / A) / A:</span> — có thể trên 1 dòng hoặc nhiều dòng<br />
              • Đánh dấu đáp án: dòng <span>Answer: B</span> hoặc đặt dấu <span>*</span> trước đáp án đúng (<span>*B. Paris</span>)<br />
              • Giải thích (tuỳ chọn): dòng bắt đầu bằng <span>//</span>
            </FormatHint>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center', marginTop: 12 }}>
              <BuildBtn style={{ margin: 0 }} onClick={handleBuild}>
                ✨ Tạo giao diện ({rawText.trim() ? `${parseQuestions(rawText).length} câu` : '0 câu'})
              </BuildBtn>
              <button
                onClick={() => setRawText(EXAMPLE)}
                style={{
                  padding: '10px 18px',
                  borderRadius: 12,
                  border: '1.5px solid rgba(165,243,252,0.35)',
                  background: 'rgba(165,243,252,0.08)',
                  color: '#a5f3fc',
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: 'pointer',
                  transition: 'all 0.18s',
                  whiteSpace: 'nowrap',
                }}
                onMouseEnter={e => (e.currentTarget.style.background = 'rgba(165,243,252,0.18)')}
                onMouseLeave={e => (e.currentTarget.style.background = 'rgba(165,243,252,0.08)')}
              >
                📄 Tải ví dụ
              </button>
            </div>
          </>
        )}
      </TeacherPanel>

      {/* Question display */}
      {questions.length > 0 && q && (
        <QuestionArea ref={questionRef}>
          {/* Progress */}
          <ProgressRow>
            <ProgressLabel>Câu {current + 1}/{questions.length}</ProgressLabel>
            <ProgressBar pct={((current + 1) / questions.length) * 100} />
            <ScorePill>⭐ {score}/{current + (selected !== null || revealed ? 1 : 0)}</ScorePill>
          </ProgressRow>

          <QuestionCard state={cardState}>
            <QuestionNumber>Câu {q.number}</QuestionNumber>
            <QuestionText>{q.question}</QuestionText>

            <OptionsGrid>
              {q.options.map((opt: string, idx: number) => {
                const status = getOptionStatus(idx);
                return (
                  <OptionBtn
                    key={idx}
                    status={status}
                    colorIdx={idx % 4}
                    onClick={() => handleSelect(idx)}
                    disabled={revealed || selected !== null}
                  >
                    <OptionLabel colorIdx={idx % 4} status={status}>
                      {OPTION_LABELS[idx]}
                    </OptionLabel>
                    {opt}
                  </OptionBtn>
                );
              })}
            </OptionsGrid>

            {/* Correct reveal */}
            {(selected !== null || revealed) && (
              <RevealBadge>
                ✅ Đáp án đúng: <strong>{OPTION_LABELS[q.answerIdx]}. {q.options[q.answerIdx]}</strong>
              </RevealBadge>
            )}

            {/* Explanation */}
            {q.explanation && (selected !== null || revealed) && (
              <ExplainBox>💡 {q.explanation}</ExplainBox>
            )}
          </QuestionCard>

          {/* Nav */}
          <NavRow>
            <NavBtn onClick={handlePrev} disabled={current === 0} variant="ghost">← Câu trước</NavBtn>
            {selected === null && !revealed && (
              <RevealBtn onClick={handleReveal}>🔍 Xem đáp án</RevealBtn>
            )}
            {current < questions.length - 1 ? (
              <NavBtn variant="primary" onClick={handleNext}>Câu tiếp →</NavBtn>
            ) : (
              <NavBtn variant="primary" onClick={handleReset} style={{ background: 'linear-gradient(135deg,#f59e0b,#ef4444)' }}>
                🔄 Làm lại từ đầu
              </NavBtn>
            )}
          </NavRow>

          {/* Results when done */}
          {current === questions.length - 1 && (selected !== null || revealed) && (
            <div style={{
              marginTop: 20, padding: '18px 24px',
              background: 'rgba(99,102,241,0.12)',
              border: '1.5px solid rgba(99,102,241,0.3)',
              borderRadius: 18, textAlign: 'center', color: '#c4b5fd',
            }}>
              <div style={{ fontSize: 32, marginBottom: 8 }}>
                {score / questions.length >= 0.8 ? '🏆' : score / questions.length >= 0.5 ? '⭐' : '📚'}
              </div>
              <div style={{ fontSize: 20, fontWeight: 800, color: '#a5b4fc' }}>
                Kết quả: {score}/{questions.length}
              </div>
              <div style={{ fontSize: 13, color: '#6366f1', marginTop: 4 }}>
                {score / questions.length >= 0.8 ? 'Xuất sắc! 🎉' :
                 score / questions.length >= 0.5 ? 'Tốt lắm, tiếp tục cố gắng!' : 'Cần ôn tập thêm 💪'}
              </div>
            </div>
          )}
        </QuestionArea>
      )}

      {questions.length === 0 && !rawText && (
        <div style={{ textAlign: 'center', color: '#475569', marginTop: 40, fontSize: 14 }}>
          📋 Dán câu hỏi vào ô bên trên và nhấn "Tạo giao diện" để bắt đầu
        </div>
      )}
    </PageBg>
  );
};

export default MultipleChoicePage;
